import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PRODUCT_OPTIONS } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", phone: "", product: "", message: "" };

const Field = ({ label, name, type = "text", value, onChange, required, testid }) => (
  <label className="block">
    <span className="font-sans-lux text-[10px] uppercase tracking-[0.25em] text-[#7C756B]">
      {label} {required && <span className="text-[#B82925]">*</span>}
    </span>
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-transparent border-b border-[#2C2621] py-3 mt-2 font-sans-lux text-base text-[#F4EFE6] placeholder:text-[#7C756B]/60 focus:border-[#D49A44] focus:outline-none transition-colors duration-300"
    />
  </label>
);

export const Inquiry = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.product || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Your inquiry has been received. We will be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="inquire"
      data-testid="inquiry-section"
      className="max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-40"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-sans-lux text-xs uppercase tracking-[0.3em] text-[#D49A44] mb-6">
            Trade Inquiry
          </p>
          <h2 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#F4EFE6] mb-8">
            Begin a <span className="italic text-[#D49A44]">conversation.</span>
          </h2>
          <p className="font-sans-lux text-base text-[#B0A89D] leading-relaxed max-w-md mb-12">
            For wholesale orders, grade specifications and bespoke sourcing, share your
            requirements. Our merchants respond to every serious inquiry personally.
          </p>

          <div className="space-y-6 border-t border-[#2C2621] pt-8">
            <div>
              <p className="font-sans-lux text-[10px] uppercase tracking-[0.25em] text-[#7C756B]">Correspondence</p>
              <a href="mailto:jivrajranchoddas@gmail.com" className="font-serif-lux text-2xl text-[#F4EFE6] hover:text-[#D49A44] transition-colors">jivrajranchoddas@gmail.com</a>
            </div>
            <div>
              <p className="font-sans-lux text-[10px] uppercase tracking-[0.25em] text-[#7C756B]">Established</p>
              <p className="font-serif-lux text-2xl text-[#F4EFE6]">Mumbai, India · 1911</p>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="inquiry-form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 lg:pl-12 space-y-10"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <Field label="Full Name" name="name" value={form.name} onChange={onChange} required testid="inquiry-name" />
            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="inquiry-email" />
            <Field label="Company" name="company" value={form.company} onChange={onChange} testid="inquiry-company" />
            <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="inquiry-phone" />
          </div>

          <label className="block">
            <span className="font-sans-lux text-[10px] uppercase tracking-[0.25em] text-[#7C756B]">
              Product of Interest <span className="text-[#B82925]">*</span>
            </span>
            <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
              <SelectTrigger
                data-testid="inquiry-product"
                className="w-full bg-transparent border-0 border-b border-[#2C2621] rounded-none px-0 py-3 mt-2 h-auto font-sans-lux text-base text-[#F4EFE6] focus:ring-0 focus:border-[#D49A44]"
              >
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1714] border-[#2C2621] text-[#F4EFE6]">
                {PRODUCT_OPTIONS.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`product-opt-${p}`} className="font-sans-lux focus:bg-[#2C2621] focus:text-[#D49A44]">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="block">
            <span className="font-sans-lux text-[10px] uppercase tracking-[0.25em] text-[#7C756B]">
              Your Requirements <span className="text-[#B82925]">*</span>
            </span>
            <textarea
              data-testid="inquiry-message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={4}
              required
              className="w-full bg-transparent border-b border-[#2C2621] py-3 mt-2 font-sans-lux text-base text-[#F4EFE6] placeholder:text-[#7C756B]/60 focus:border-[#D49A44] focus:outline-none transition-colors duration-300 resize-none"
              placeholder="Quantities, grades, destination…"
            />
          </label>

          <button
            type="submit"
            data-testid="inquiry-submit-btn"
            disabled={loading}
            className="btn-lux inline-flex items-center justify-center border border-[#D49A44] px-10 py-4 font-sans-lux text-xs uppercase tracking-[0.2em] text-[#D49A44] disabled:opacity-50"
          >
            {loading ? "Sending…" : "Submit Inquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
